// ============================================
// Patient Journey Engine (PJE) Type Definitions
//
// Simulation system for testing AI nurse
// conversations across patient personas,
// touchpoints, and load scenarios.
// ============================================

import type { CapturedSignal } from './fashionPlate';

// --- Shared Primitives ---

export interface Demographics {
  name: string;
  age: number;
  sex: 'Male' | 'Female';
  state: string;
  zipCode?: string;
}

export interface LabValue {
  biomarker: string;
  value: number;
  unit: string;
  conventionalRange: string;
  functionalRange: string;
  xPrizeRange?: string;
  status: 'below_conventional' | 'below_functional' | 'optimal'
       | 'above_functional' | 'above_conventional' | 'elevated';
  trigger: boolean;
  timestamp: string;
}

export interface Medication {
  name: string;
  dose: string;
  category: string;
  duration: string;
  interactionDbId?: string;
}

export interface CheckItem {
  checkId: string;
  check: string;
  category: 'clinical' | 'conversational' | 'compliance' | 'pricing'
         | 'register' | 'modifier' | 'constraint';
  severity: 'critical' | 'major' | 'minor';
  automatable: boolean;
}

export interface CheckResult {
  checkId: string;
  pass: boolean | null;
  scoredBy: 'manual' | 'ai_twin';
  scoredAt: string;
  notes?: string;
}

export interface Message {
  role: 'patient' | 'ai_nurse' | 'nurse' | 'analyst' | 'guide' | 'system';
  content: string;
  timestamp: string;
  touchpointId: string;
  contextSnapshot?: ContextLayer;
  metadata?: MessageMetadata;
}

export interface MessageMetadata {
  tokensUsed?: number;
  latencyMs?: number;
  modelVersion?: string;
  registerActive?: RegisterType;
  modifiersDetected?: string[];
  sentimentScore?: number;
}

// --- Context Layer (17 variables, 6 categories) ---

export interface ContextLayer {
  // Temporal
  timeOfDay: 'morning' | 'afternoon' | 'evening' | 'late_night';
  cyclePhase: 'follicular' | 'ovulatory' | 'luteal' | 'menstrual'
           | 'postmenopausal' | 'not_applicable';
  season: 'spring' | 'summer' | 'fall' | 'winter';
  timeSinceLastInteraction: 'first_visit' | 'days' | 'weeks' | 'months';
  // Emotional
  baselineStress: 'low' | 'moderate' | 'high' | 'crisis';
  recentLifeEvent: 'none' | 'divorce' | 'job_loss' | 'bereavement'
                 | 'new_diagnosis' | 'new_baby' | 'relocation';
  motivation: 'desperate' | 'concerned' | 'curious' | 'proactive' | 'skeptical';
  // Relational
  partnerStatus: 'single' | 'partnered' | 'divorced' | 'widowed';
  caregiverBurden: 'none' | 'light' | 'heavy';
  socialSupport: 'strong' | 'moderate' | 'isolated';
  // Financial
  budgetSensitivity: 'low' | 'moderate' | 'high' | 'severe';
  insuranceContext: 'insured' | 'uninsured' | 'hsa_fsa_eligible';
  // Literacy
  healthKnowledge: 'minimal' | 'basic' | 'informed' | 'expert';
  supplementExperience: 'none' | 'some' | 'extensive';
  aiComfort: 'distrustful' | 'cautious' | 'neutral' | 'comfortable';
  // Clinical
  journeyStage: 'first_visit' | 'on_protocol' | 'retesting'
             | 'post_escalation' | 're_engagement';
  adherenceLevel: 'high' | 'moderate' | 'low' | 'unknown';
}

export const DEFAULT_CONTEXT: ContextLayer = {
  timeOfDay: 'morning',
  cyclePhase: 'not_applicable',
  season: 'spring',
  timeSinceLastInteraction: 'first_visit',
  baselineStress: 'moderate',
  recentLifeEvent: 'none',
  motivation: 'curious',
  partnerStatus: 'partnered',
  caregiverBurden: 'none',
  socialSupport: 'moderate',
  budgetSensitivity: 'moderate',
  insuranceContext: 'insured',
  healthKnowledge: 'basic',
  supplementExperience: 'none',
  aiComfort: 'neutral',
  journeyStage: 'first_visit',
  adherenceLevel: 'unknown',
};

// --- Journey & Touchpoint ---

export interface Touchpoint {
  touchpointId: string;
  type: 'first_contact' | 'checkout' | 'mid_protocol'
      | 'retest' | 'med_change' | 'gate2_transition'
      | 'protocol_success' | 'cancellation' | 're_engagement';
  order: number;
  label: string;
  trigger: string;
  stateOverrides?: Partial<TouchpointState>;
  conversationScript: ConversationTurn[];
  validationChecklist: CheckItem[];
}

export interface ConversationTurn {
  speaker: 'patient' | 'expected_nurse';
  text: string;
}

export interface TouchpointState {
  touchpointId: string;
  personaSnapshot: TestPersonaSnapshot;
  labsAtEntry: LabValue[];
  medicationsAtEntry: Medication[];
  gateStatus: 1 | 2;
  protocolDays: number;
  previousTouchpoints: string[];
  subscriptionStatus: 'none' | 'active' | 'paused' | 'cancelled';
  adherenceScore: number;
}

export type TestPersonaSnapshot = Omit<TestPersona, 'journeyTouchpoints' | 'validationChecklist' | 'twinConfig'>;

// --- AI Twin ---

export interface AITwinConfig {
  twinId: string;
  behaviorModel: BehaviorModel;
  maxTurnsPerTouchpoint: number;
  evaluationCriteria: string[];
  apiConfig: TwinApiConfig;
}

export interface BehaviorModel {
  communicationStyle: 'terse' | 'conversational' | 'verbose';
  questionFrequency: 'few' | 'moderate' | 'many';
  attentionSpan: 'short' | 'medium' | 'long';
  decisionSpeed: 'impulsive' | 'moderate' | 'deliberate';
  skepticismLevel: number;
  complianceTendency: number;
  priceResistance: number;
  emotionalExpressiveness: number;
  healthLiteracy?: number;
  objectionPatterns: {
    pattern: 'price' | 'trust' | 'evidence' | 'time' | 'efficacy';
    probability: number;
  }[];
  dataSource: 'manual' | 'learned';
  dataVersion: number;
  sampleSize?: number;
  // v3.1: Modifier reveal behavior for twin testing
  modifierRevealPattern?: ModifierRevealPattern;
}

export interface TwinApiConfig {
  endpoint: string;
  authMethod: 'service_account' | 'test_token';
  serviceAccountId?: string;
  testToken?: string;
  timeoutMs: number;
  retryPolicy: {
    maxRetries: number;
    backoffMs: number;
    backoffMultiplier: number;
  };
  environment: 'test' | 'staging';
}

// --- Load Simulation ---

export interface PersonaMixConfig {
  cleanGate1: number;
  drugInteraction: number;
  gate2Escalation: number;
  stateRestriction: number;
  multiProtocol: number;
  exclusionCriteria: number;
  skeptic: number;
  overOptimizer: number;
  custom: number;
}

export interface ContextDistConfig {
  timeOfDay: { morning: number; afternoon: number; evening: number; late_night: number; };
  stressLevel: { low: number; moderate: number; high: number; crisis: number; };
  healthKnowledge: { minimal: number; basic: number; informed: number; expert: number; };
  budgetSensitivity: { low: number; moderate: number; high: number; severe: number; };
}

export interface LoadSimConfig {
  tier: 'baseline' | 'growth' | 'surge' | 'viral' | 'stress';
  concurrentUsers: number;
  rampUpSeconds: number;
  sustainSeconds: number;
  rampDownSeconds: number;
  personaMix: PersonaMixConfig;
  contextDistribution: ContextDistConfig;
  touchpointDistribution: {
    firstContact: number;
    midProtocol: number;
    retest: number;
    gate2: number;
    cancellation: number;
  };
  metrics: {
    sampleIntervalMs: number;
    alertThresholds: {
      p95LatencyMs: number;
      errorRatePercent: number;
      sessionLeakCount: number;
    };
  };
  tooling: 'k6' | 'artillery' | 'custom_cloud_function';
}

export interface LoadSimRun {
  runId: string;
  brandId: string;
  config: LoadSimConfig;
  startedAt: string;
  completedAt?: string;
  status: 'running' | 'completed' | 'failed' | 'aborted';
  results: {
    peakConcurrency: number;
    totalRequests: number;
    totalErrors: number;
    errorRate: number;
    latency: { p50: number; p95: number; p99: number; };
    sessionLeaks: number;
    protocolAccuracyRate: number;
    breakingPoint?: number;
    costEstimate: {
      medgemmaInferenceCost: number;
      firestoreReadWriteCost: number;
      cloudRunComputeCost: number;
      totalEstimatedMonthlyCost: number;
    };
  };
}

// --- Firestore Document Interfaces ---

export interface TestSuite {
  suiteId: string;
  brandId: string;
  vertical: string;
  generatedFrom: 'archetype' | 'custom' | 'ai_twin';
  version: number;
  previousVersionId?: string;
  createdAt: string;
  updatedAt: string;
  personas: PersonaRef[];
  readinessScore: number;
  lastRunAt?: string;
  status: 'draft' | 'active' | 'archived' | 'regenerating';
}

export interface PersonaRef {
  personaId: string;
  name: string;
  archetype: string;
  lastPassRate: number;
}

export interface TestPersona {
  personaId: string;
  name: string;
  archetype: string;
  demographics: Demographics;
  labs: LabValue[];
  medications: Medication[];
  conditions: string[];
  contraindications: string[];
  allergens: string[];
  contextModifiers: ContextLayer;
  journeyTouchpoints: Touchpoint[];
  validationChecklist: CheckItem[];
  twinConfig?: AITwinConfig;
}

export interface TestRun {
  runId: string;
  suiteId: string;
  personaId: string;
  touchpointId: string;
  startedAt: string;
  completedAt?: string;
  status: 'in_progress' | 'completed' | 'failed' | 'timed_out';
  error?: { code: string; message: string; retryable: boolean; };
  checkResults: CheckResult[];
  conversationLog: Message[];
  scorer: 'manual' | 'ai_twin';
  passRate: number;
  suiteVersion: number;
}

export interface AITwin {
  twinId: string;
  sourceType: 'synthetic' | 'anonymized_real';
  persona: TestPersona;
  behaviorModel: BehaviorModel;
  journeyHistory: JourneyEvent[];
  dataVersion: number;
}

export interface JourneyEvent {
  eventId: string;
  touchpointType: string;
  timestamp: string;
  outcome: 'completed' | 'abandoned' | 'error';
  passRate: number;
  runId: string;
}

// --- Panel Generator ---

export interface PanelGeneratorConfig {
  tier: 1 | 2 | 3 | 4 | 5;
  demographics: Demographics;
  verticals: string[];
  conditions?: string[];
  triggerTargets?: {
    biomarkerId: string;
    targetStatus: 'below_functional' | 'above_functional'
                | 'below_conventional' | 'above_conventional';
  }[];
  healthProfile?: 'optimal' | 'mixed' | 'concerning' | 'critical';
  seed?: number;
}

export interface GeneratedPanel {
  panelId: string;
  tier: number;
  generatedAt: string;
  seed: number;
  demographics: Demographics;
  labs: LabValue[];
  correlationsApplied: string[];
  clinicalNarrative: string;
  triggerBiomarkers: string[];
  expectedProtocols: string[];
  expectedProducts: string[];
  multiVerticalTriggers: string[];
}

export interface LongitudinalConfig extends PanelGeneratorConfig {
  tier: 5;
  drawCount: 2 | 3 | 4;
  drawIntervalDays: number;
  progressionPattern: 'improvement' | 'plateau' | 'worsening' | 'mixed' | 'new_variable';
  interventionBetweenDraws?: {
    afterDraw: number;
    type: 'new_medication' | 'supplement_adherence_change' | 'lifestyle_change' | 'dosage_adjustment';
    details: string;
  }[];
}

export interface LongitudinalPanel {
  panelSetId: string;
  draws: GeneratedPanel[];
  progressionPattern: string;
  interventions: string[];
  expectedJourneyPath: string[];
  expectedGateTransitions: {
    fromGate: 1 | 2;
    toGate: 1 | 2;
    afterDraw: number;
    reason: string;
  }[];
}

// --- Biomarker Catalog ---

export interface BiomarkerCorrelation {
  primaryBiomarker: string;
  correlatedBiomarker: string;
  relationship: 'positive' | 'inverse' | 'conditional';
  strength: 'strong' | 'moderate' | 'weak';
  rule: string;
  adjustmentFn: string;
}

export interface BiomarkerDistribution {
  biomarkerId: string;
  demographicFilter: {
    sex?: 'Male' | 'Female';
    ageRange?: { min: number; max: number; };
    conditions?: string[];
  };
  distribution: {
    mean: number;
    stdDev: number;
    skew?: number;
    floor: number;
    ceiling: number;
  };
  conventionalRange: { low: number; high: number; unit: string; };
  functionalRange: { low: number; high: number; unit: string; };
}

export type BiomarkerPanelCategory =
  | 'cbc' | 'cmp' | 'lipid_standard' | 'lipid_advanced'
  | 'thyroid' | 'hormones_women' | 'hormones_men' | 'fertility'
  | 'metabolic' | 'nutrients' | 'inflammation' | 'autoimmune'
  | 'heavy_metals' | 'cancer_markers' | 'growth_aging';

export interface CatalogBiomarker {
  id: string;
  name: string;
  unit: string;
  categories: BiomarkerPanelCategory[];
  tierMin: 1 | 2 | 3 | 4;
  gender: 'Male' | 'Female' | 'any';
  conventionalRange: { low: number; high: number; };
  functionalRange: { low: number; high: number; };
  xPrizeRange: { low: number; high: number; };
  distribution: {
    mean: number;
    stdDev: number;
    skew?: number;
    floor: number;
    ceiling: number;
  };
  /** Optional age-dependent ranges for synthetic data generation */
  ageBrackets?: Array<{
    minAge: number;
    maxAge: number;
    conventionalRange: { low: number; high: number | null };
    functionalRange: { low: number; high: number | null };
    xPrizeRange: { low: number; high: number | null };
  }>;
}

export interface RealPanelIngestion {
  panelId: string;
  source: 'clinical_practice' | 'case_study' | 'patient_upload' | 'function_health_export';
  deidentified: boolean;
  consentRecordId?: string;
  originalFormat: 'csv' | 'hl7' | 'fhir' | 'pdf_parsed' | 'manual';
  labs: LabValue[];
  knownDiagnosis?: string;
  clinicalNotes?: string;
}

// --- Conversation Mode (v2 — retained for backward compat) ---

export type ConversationMode = 'high_empathy' | 'simplified' | 'evidence' | 'budget_conscious' | 'continuity';

// ============================================
// v3.1 Bifurcated Persona System
// ============================================

// --- Registers ---

export type RegisterType = 'analyst' | 'guide' | 'blended';
export type NursePhase = 'phase1_scripted' | 'phase2_freeform';
export type ModifierRevealPattern = 'upfront' | 'gradual' | 'withholding' | 'forgetful';

// --- Nurse Trait Profile (TARS-style calibration) ---

export interface NurseTraitProfile {
  empathy: number;              // 0-100, Guide register intensity
  dataDensity: number;          // 0-100, biomarker refs per response
  explanationDepth: number;     // 0-100, jargon threshold
  productTiming: number;        // 0-100, turn # to introduce formulary (0=early, 100=late)
  verificationFreq: number;     // 0-100, follow-up question frequency
  urgencyCalibration: number;   // 0-100, phase transition speed (0=calm, 100=urgent)
}

export const DEFAULT_TRAIT_PROFILE: NurseTraitProfile = {
  empathy: 70,
  dataDensity: 60,
  explanationDepth: 50,
  productTiming: 40,
  verificationFreq: 50,
  urgencyCalibration: 30,
};

// --- Severity ---

export type SeverityLevel = 'routine' | 'attention' | 'urgent' | 'critical';

// --- Analyst Phase 1 ---

export interface BiomarkerCard {
  biomarkerId: string;
  name: string;
  value: number;
  unit: string;
  standardRange: { low: number; high: number };
  functionalRange: { low: number; high: number };
  optimalRange: { low: number; high: number };  // MVP: same as functionalRange
  status: 'optimal' | 'suboptimal' | 'low' | 'elevated' | 'critical';
  trigger: boolean;
}

export interface AnalystPhase1Output {
  biomarkerCards: BiomarkerCard[];
  severity: SeverityLevel;
  triggeredCount: number;
  criticalMarkers: string[];
}

// --- Pregnancy / Reproductive Status Data (collected via PregnancyStatusModal) ---

export type BiologicalSex = 'male' | 'female';

export interface PregnancyData {
  biologicalSex: BiologicalSex;
  isPregnant: boolean;
  isTryingToConceive: boolean;
  isBreastfeeding: boolean;
}

// --- Modifier Set (collected via modals in Phase 1) ---

export interface ModifierSet {
  allergies: string[];
  medications: Medication[];
  conditions: string[];
  lifestyle: string[];
  budgetTier: 'tier1_only' | 'tier1_tier2' | 'all_tiers';
  intent: 'exploring' | 'motivated_buyer' | 'urgent';
  reproductiveStatus: 'not_applicable' | 'trying_to_conceive' | 'pregnant'
                    | 'breastfeeding' | 'postmenopausal';
  priorSupplements: string[];
  contraindications: string[];
  goals: string[];
}

export const EMPTY_MODIFIER_SET: ModifierSet = {
  allergies: [],
  medications: [],
  conditions: [],
  lifestyle: [],
  budgetTier: 'all_tiers',
  intent: 'exploring',
  reproductiveStatus: 'not_applicable',
  priorSupplements: [],
  contraindications: [],
  goals: [],
};

// --- Analyst Phase 2 (Protocol) ---

export interface ProtocolItem {
  productId: string;
  productName: string;
  dosage: string;
  timing: 'am_energy' | 'midday_absorption' | 'pm_recovery';
  reasoning: string;
  triggerBiomarker: string;
  tier: 1 | 2 | 3;
  blocked?: { reason: string };
}

export interface AnalystPhase2Output {
  protocol: ProtocolItem[];
  blockedProducts: { productName: string; reason: string }[];
  warnings: { productName: string; message: string }[];
  modifiersApplied: ModifierSet;
  gateLevel: 1 | 2;
}

// --- Conversation State (tracks Two-Mode Nurse state) ---

export interface ConversationState {
  phase: NursePhase;
  activeRegister: RegisterType;
  turnNumber: number;
  modifiers: ModifierSet;
  sentimentTrajectory: number[];
  sentimentBaseline: number;
  phase1Complete: boolean;
  protocolPresented: boolean;
  lateReveals: { turn: number; modifier: string; category: 'safety_critical' | 'preference' }[];
  phase2RerunCount: number;
  capturedSignals: CapturedSignal[];
}

export const INITIAL_CONVERSATION_STATE: ConversationState = {
  phase: 'phase1_scripted',
  activeRegister: 'analyst',
  turnNumber: 0,
  modifiers: { ...EMPTY_MODIFIER_SET },
  sentimentTrajectory: [],
  sentimentBaseline: 50,
  phase1Complete: false,
  protocolPresented: false,
  lateReveals: [],
  phase2RerunCount: 0,
  capturedSignals: [],
};

// --- Register Evaluation (Firestore doc) ---

export interface RegisterEvaluation {
  evalId: string;
  runId: string;
  register: RegisterType;
  phase: NursePhase;
  turnRange: { start: number; end: number };
  checks: {
    dataAccuracy: boolean;        // Analyst: min 3 biomarker value refs
    toneCompliance: boolean;      // No register drift
    contextAdaptation: boolean;   // Matches context layer
    registerIsolation: boolean;   // Analyst doesn't emote, Guide doesn't dump data
  };
  score: number;                  // 0-100
  notes?: string;
}

// --- Modifier Flow (Firestore doc) ---

export interface ModifierFlow {
  flowId: string;
  runId: string;
  phase1Modifiers: ModifierSet;           // From modals
  phase2LateReveals: {
    turn: number;
    rawMessage: string;
    extractedModifiers: Partial<ModifierSet>;
    category: 'safety_critical' | 'preference';
    phase2Rerun: boolean;
  }[];
  completeness: number;                   // 0-100
  safetyCriticalCovered: boolean;
  phase2RerunCount: number;
}

// --- Twin Run Result (v3.1 with error handling) ---

export interface TwinRunResult {
  status: 'completed' | 'failed' | 'timeout' | 'aborted';
  error?: {
    code: string;
    message: string;
    retryable: boolean;
    occurredAt: string;
    turnNumber?: number;
  };
  retryCount: number;
  maxRetries: number;
  timeoutMs: number;
}
